import { useContext } from 'react';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { Avatar, Button, Flex, Table, Tag, Tooltip } from 'antd';
import { userRoleAsString, EProductType } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import loginAsUser from '../../../domain/use_cases/users/loginAsUser';
import UserPartnerLogo from '../user_partner_logo/UserPartnerLogo';
import { IUser } from '@leedpulse-monorepo/interfaces';
import { advancedOnRow } from '../../../core/utils/commonUtils';
import { USERS_PAGE_PATH } from '../../pages/users/UsersPage';
import { useNavigate } from 'react-router-dom';

import { LeedflowLogoMinimalist, LeedpulseLogoMinimalist } from '@leedpulse-monorepo/assets';

interface UsersListProps {
  users: Record<string, any>[];
  loading: boolean;
  page: number;
  nbHits: number;
  hitsPerPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
}

const UsersList: React.FC<UsersListProps> = ({ users, loading, page, nbHits, hitsPerPage, onPageChange, onPageSizeChange }) => {
  const authContext = useContext(AuthenticationContext);
  const windowSize = useWindowSize();
  const navigate = useNavigate();

  const handleConnectToUser = async (event: React.MouseEvent<HTMLElement>, userUid: string, product: EProductType) => {
    event.stopPropagation();
    if (authContext.authentication) {
      const response = await loginAsUser(userUid, product, authContext.authentication);
      if (response.success) {
        window.open(response.data.auth_url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    onPageSizeChange?.(newPageSize);
  };

  return (
    <Table
      pagination={{
        current: page + 1,
        total: nbHits,
        pageSize: hitsPerPage,
        onChange: (p) => onPageChange(p - 1),
        onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
        showSizeChanger: true,
        simple: true,
        pageSizeOptions: [20, 50, 100, 200],
        position: ['bottomRight', 'topRight'],
        disabled: loading,
      }}
      loading={loading}
      columns={
        windowSize.width > 576
          ? [
            {
              title: 'Avatar',
              dataIndex: 'avatar',
              key: 'avatar',
              render: (avatar: string) => <Avatar src={avatar} size={'small'} />,
            },
            {
              title: 'Utilisateur',
              render: (record) => (
                <><UserPartnerLogo partnerSystem={record?.partner_system} /> {record?.firstname} {record?.lastname}</>
              ),
            },
            {
              title: 'Email',
              dataIndex: 'email',
              key: 'email',
            },
            {
              title: 'Abonnements',
              dataIndex: 'id_subscription',
              key: 'id_subscription',
              render: (idSubscription) => Object.keys(idSubscription ?? {}).length,
            },
            {
              title: 'Rôle',
              dataIndex: 'role',
              key: 'role',
              render: (role: string) => userRoleAsString(role),
            },
            {
              title: 'Canal',
              dataIndex: 'voip_enabled',
              key: 'voip_enabled',
              render: (voipEnabled: boolean) =>
                voipEnabled ? <Tag color="green">VoIP</Tag> : <Tag>GSM</Tag>,
            },
            {
              title: 'Actions',
              dataIndex: 'uid',
              key: 'uid',
              render: (uid: string, record) => (
                <Flex gap="0.5rem">
                  <Tooltip title={`Se connecter en tant que ${record?.firstname} ${record?.lastname} sur ${EProductType.LEEDFLOW.toUpperCase()}`}>
                    <Button
                      onClick={(e) => handleConnectToUser(e, uid, EProductType.LEEDFLOW)}
                      icon={
                        <Flex align='center' justify='center'>
                          <img src={LeedflowLogoMinimalist} alt={EProductType.LEEDFLOW} style={{ height: '18px' }} />
                        </Flex>
                      }
                      color='primary'
                    />
                  </Tooltip>

                  <Tooltip title={`Se connecter en tant que ${record?.firstname} ${record?.lastname} sur ${EProductType.LEEDPULSE.toUpperCase()}`}>
                    <Button
                      onClick={(e) => handleConnectToUser(e, uid, EProductType.LEEDPULSE)}
                      icon={
                        <Flex align='center' justify='center'>
                          <img src={LeedpulseLogoMinimalist} alt={EProductType.LEEDPULSE} style={{ height: '18px' }} />
                        </Flex>
                      }
                      color='orange'
                    />
                  </Tooltip>
                </Flex>
              ),
            },
          ]
          : [
            {
              title: 'Email',
              dataIndex: 'email',
              key: 'email',
            },
          ]
      }
      rowKey={(record) => record.objectID ?? record.uid}
      dataSource={users as (IUser & { objectID: string })[]}
      onRow={(record) =>
        advancedOnRow({
          url: `${USERS_PAGE_PATH}/${record.uid}`,
          navigate,
          className: 'table-row',
        })
      }
    />
  );
};

export default UsersList;
